/**
 * FlowSettingsDialog — shared style objects.
 *
 * Extracted from FlowSettingsDialog.tsx to keep the component file focused on
 * its tab sections and logic. Pure CSSProperties presets on the shared theme.
 */

import React from 'react';
import { color, font, z } from '../../components/ui/theme';

// ── Styles ────────────────────────────────────────────────────────────────────

// Minimalist glass window — same look as the "Open Beatmap" dialog: big rounded
// corners, blurred translucent surface, soft shadow + faint accent ring.
export const windowStyle: React.CSSProperties = {
  position: 'fixed',
  zIndex: z.modal,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  borderRadius: 18,
  background: 'oklch(0.10 calc(0.018 * var(--tint-c)) var(--tint-h) / 0.86)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.28)',
  backdropFilter: 'blur(24px)',
  WebkitBackdropFilter: 'blur(24px)',
  boxShadow: '0 24px 70px rgba(0, 0, 0, 0.6), 0 0 30px oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.12)',
};

export const titleBarStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: 44,
  padding: '0 12px 0 16px',
  cursor: 'move',
  userSelect: 'none',
  borderBottom: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.14)',
};

export const titleInnerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: 1.2,
  textTransform: 'uppercase',
  color: color.accent,
};

export const closeBtnStyle: React.CSSProperties = {
  width: 28,
  height: 28,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  background: 'transparent',
  // Accent ring + accent X at rest; hover fills with accent (X turns dark tint).
  border: '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.55)',
  color: 'var(--accent)',
  cursor: 'pointer',
  fontSize: 12,
  lineHeight: 1,
  flexShrink: 0,
  fontFamily: 'inherit',
  transition: 'background 0.15s ease, color 0.15s ease, border-color 0.15s ease',
};

export const bodyStyle: React.CSSProperties = {
  display: 'flex',
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
};

export const tabListStyle: React.CSSProperties = {
  width: 120,
  flexShrink: 0,
  borderRight: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.15)',
  display: 'flex',
  flexDirection: 'column',
  padding: '8px 6px',
  gap: 3,
  overflowY: 'auto',
};

export const tabBtnStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 12px',
  borderRadius: 999,
  border: '1px solid',
  cursor: 'pointer',
  fontFamily: font.sans,
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: 0.5,
  textAlign: 'left',
  transition: 'all 0.12s ease',
};

export const contentStyle: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  minHeight: 0,
};

export const tabContentStyle: React.CSSProperties = {
  padding: 18,
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
};

export const sectionBoxStyle: React.CSSProperties = {
  // Match the sidebar panel cards' darkness (FlowApp.styles groupStyle).
  background: 'oklch(0.065 calc(0.016 * var(--tint-c)) var(--tint-h) / 0.78)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.18)',
  borderRadius: 10,
  padding: '10px 14px 12px',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

export const sectionTitleStyle: React.CSSProperties = {
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: 1.4,
  color: color.accentSoft,
  textTransform: 'uppercase',
  borderBottom: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.12)',
  paddingBottom: 4,
  marginBottom: 2,
};

export const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  minHeight: 26,
};

export const rowLabelStyle: React.CSSProperties = {
  fontSize: 10,
  color: color.inkDim,
  letterSpacing: 0.4,
  minWidth: 90,
  flexShrink: 0,
};

export const stepBtn: React.CSSProperties = {
  background: 'oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.18)',
  border: '1px solid oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.28)',
  color: color.accent,
  borderRadius: 5,
  width: 22,
  height: 22,
  cursor: 'pointer',
  fontSize: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  fontFamily: 'inherit',
};

export const stepBtnSm: React.CSSProperties = { ...stepBtn, width: 18, fontSize: 7, color: '#9F7AEA' };

export const stepValStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: color.ink,
  textAlign: 'center',
  minWidth: 56,
  fontVariantNumeric: 'tabular-nums',
};

export const presetBtnStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  padding: '8px 12px',
  borderRadius: 8,
  border: '1px solid',
  cursor: 'pointer',
  fontFamily: font.sans,
  fontSize: 11,
  textAlign: 'left',
  transition: 'all 0.12s ease',
};

export const actionBtnStyle: React.CSSProperties = {
  padding: '8px 12px',
  borderRadius: 8,
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.3)',
  background: 'linear-gradient(135deg, oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.25), oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.15))',
  color: '#D8B4FE',
  fontFamily: font.sans,
  fontSize: 10,
  fontWeight: 600,
  cursor: 'pointer',
  textAlign: 'center',
};

export const hintTextStyle: React.CSSProperties = {
  fontSize: 10,
  color: color.inkFaint,
  lineHeight: 1.5,
  margin: 0,
};

export const resetBtnStyle: React.CSSProperties = {
  padding: '3px 10px',
  borderRadius: 6,
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.25)',
  background: 'oklch(0.20 calc(0.030 * var(--tint-c)) var(--tint-h) / 0.6)',
  color: 'var(--accent-dim)',
  fontFamily: font.sans,
  fontSize: 9,
  fontWeight: 700,
  cursor: 'pointer',
  letterSpacing: 0.4,
};

export const keyRowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 12, padding: '6px 0',
  borderBottom: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.08)',
};
export const kbdButtonStyle: React.CSSProperties = {
  flexShrink: 0, minWidth: 84, padding: '6px 10px', borderRadius: 6,
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.4)', background: 'oklch(0.20 calc(0.030 * var(--tint-c)) var(--tint-h) / 0.7)',
  color: '#C4B5FD', fontFamily: 'monospace', fontSize: 11, fontWeight: 700,
  cursor: 'pointer', textAlign: 'center', letterSpacing: 0.3,
};
export const kbdCapturingStyle: React.CSSProperties = {
  ...kbdButtonStyle,
  background: 'linear-gradient(135deg,var(--accent-dim),#22d3ee)', color: '#fff',
  border: '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.6)',
};
export const keyTdStyle: React.CSSProperties = {
  width: 140,
  paddingBottom: 6,
  verticalAlign: 'top',
};

export const kbdStyle: React.CSSProperties = {
  display: 'inline-block',
  padding: '2px 6px',
  borderRadius: 4,
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.3)',
  background: 'oklch(0.20 calc(0.030 * var(--tint-c)) var(--tint-h) / 0.7)',
  color: '#C4B5FD',
  fontFamily: 'monospace',
  fontSize: 9,
  letterSpacing: 0.3,
};

export const descTdStyle: React.CSSProperties = {
  fontSize: 10,
  color: color.inkDim,
  paddingBottom: 6,
  verticalAlign: 'top',
};

export const resizeHandleStyle: React.CSSProperties = {
  position: 'absolute',
  bottom: 4,
  right: 4,
  width: 16,
  height: 16,
  cursor: 'nwse-resize',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
